import { useEffect, useRef, useState } from 'react';
import api from '../../../../api';
import BookConsultation from '../../../components/BookConsultation';
import Button from '../../../components/Button';
import ButtonContainer from '../../../components/ButtonContainer';
import Modal from '../../../components/Modal';
import RegisterFlowTemplate from '../../../components/RegisterFlowTemplate';
import SaveButton from '../../../components/SaveButton';
import { useAppContext } from '../../../hooks/useAppContext';
import { goToDashboardPage } from '../../../services/GoTo';
import { initIntercom } from '../../../services/Intercom';
import styles from '../styles.m.less';
import styles2 from './styles.m.less';

const BUTTON_LABELS = {
    SAVING: 'Setting up…',
    ENABLED: 'Go to Homepage',
};

export default function OnboardingSurveyDone() {
    const { user } = useAppContext();
    const [isDone, setIsDone] = useState(false);
    const [progress, setProgress] = useState(0);
    const [onboardingSettingsHris, setOnboardingSettingsHris] = useState<string | undefined>(undefined);
    const isDoneRef = useRef(isDone);
    const [showConsultation, setShowConsultation] = useState(user.company.server !== 'prod-eu');
    const REFRESH_INTERVAL = 30000;

    useEffect(() => {
        api.onboardingSettings.getOnboardingSettings().then(onboardingSettings => {
            if (onboardingSettings) {
                setOnboardingSettingsHris(onboardingSettings?.hris);
            }
        });
    }, []);

    useEffect(() => {
        let progressInterval: NodeJS.Timeout;

        function startProgress() {
            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const percentage = Math.min((elapsed / REFRESH_INTERVAL) * 100, 95);
                setProgress(prev => (isDoneRef.current && prev >= 95 ? 100 : percentage));
            }, 200);
        }

        async function refreshStatus() {
            const isCreatingSampleContent = await api.sampleContent.getStatus();
            if (!isCreatingSampleContent) {
                setProgress(100);
                setIsDone(true);
                isDoneRef.current = true;
                clearInterval(progressInterval);
                clearInterval(pollInterval);
            }
        }

        startProgress();
        const pollInterval = setInterval(refreshStatus, 30000);
        refreshStatus();

        return () => {
            clearInterval(progressInterval);
            clearInterval(pollInterval);
        };
    }, []);

    const handleSaveButtonClick = () => {
        // Don't remove, needed for addUsers modal
        localStorage.setItem(
            'small-improvements.*.onboarding',
            JSON.stringify({ value: 'onboarding', newUser: true, timestamp: new Date().getTime() })
        );
        goToDashboardPage();
        if (onboardingSettingsHris) {
            initIntercom();
        }
    };

    return (
        <RegisterFlowTemplate
            title={isDone ? 'You’re all set!' : 'Setting up your account'}
            illustration={<Illustration />}
            sidebarClasses={styles2.surveyDoneSidebar}
        >
            <Consultation showConsultation={showConsultation} onCloseConsultation={() => setShowConsultation(false)} />

            <div className={styles2.progressBarContainer}>
                <div className={styles2.progressBar} style={{ width: `${progress}%` }} />
            </div>

            <p>
                We want you to test properly, so we're setting up sample users with sample reviews, sample objectives,
                and more. This way you can explore without needing to create content on your own. You can delete the
                sample content once you're done. Here's a short video on how to make the most of your trial:
            </p>

            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <iframe
                    loading="lazy"
                    src="https://player.vimeo.com/video/1137074016?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;portrait=0&amp;byline=0&amp;title=0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                    }}
                    title="Starting your Small Improvements Trial"
                ></iframe>
            </div>

            <div className={styles.formButtonContainer}>
                <SaveButton
                    state={isDone ? 'ENABLED' : 'SAVING'}
                    labels={BUTTON_LABELS}
                    disabled={!isDone}
                    type="button"
                    onClick={handleSaveButtonClick}
                />
            </div>
        </RegisterFlowTemplate>
    );
}

function Consultation({
    showConsultation,
    onCloseConsultation,
}: {
    showConsultation: boolean;
    onCloseConsultation: () => void;
}) {
    return (
        <Modal isOpen={showConsultation} title="Book a free consultation" onClose={onCloseConsultation}>
            <p className={styles2.consultationText}>
                Book a free consultation below with our team so we can better understand your unique needs around
                performance management.
            </p>
            <BookConsultation />
            <ButtonContainer>
                <Button onClick={onCloseConsultation}>Close</Button>
            </ButtonContainer>
        </Modal>
    );
}

const ILLUSTRATION_USERS = [
    { id: 'a', name: 'Chris CEO', logo: '/images/characters/chris.svg' },
    { id: 'b', name: 'Mary Manager', logo: '/images/characters/mary.svg' },
    { id: 'c', name: 'Sam Sales', logo: '/images/characters/sam.svg' },
    { id: 'd', name: 'Dan Developer', logo: '/images/characters/dan.svg' },
    { id: 'e', name: 'Heather HR', logo: '/images/characters/heather.svg' },
    { id: 'f', name: 'Irene Intern', logo: '/images/characters/irene.svg' },
    { id: 'g', name: 'Stan Sales', logo: '/images/characters/stan.svg' },
    { id: 'h', name: 'Daphne Developer', logo: '/images/characters/daphne.svg' },
    { id: 'i', name: 'Dave DevOps', logo: '/images/characters/dave.svg' },
];

function Illustration() {
    return (
        <>
            <h3 className={styles2.illustrationHeading}>Your Test Company:</h3>
            <div className={styles2.illustration}>
                {ILLUSTRATION_USERS.map(({ id, name, logo }) => (
                    <div key={id} className={styles2.illustrationCharacter}>
                        <img alt="" src={logo} className={styles2.illustrationCharacterImage} />
                        <span>{name}</span>
                    </div>
                ))}
            </div>
        </>
    );
}
